import {
  BufferAttribute,
  Color,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  type Scene,
} from 'three';
import { fbm, clamp, smoothstep } from '../utils/rng';
import {
  basinRadius,
  terrainHeight,
  terrainNormal,
  washCenterX,
  WORLD_SIZE,
} from './heightfield';

/**
 * Maillage du sol, construit depuis `terrainHeight`, colore par sommet selon
 * la pente, l'altitude et la proximite du lit asseche. Aucune texture a
 * charger. cf. doc/02-terrain-et-bruit.md
 */

/**
 * 2 metres par quad. Les crateres font 8 a 14 m de rayon : en dessous de
 * cette densite leurs levres deviennent des facettes visibles, et surtout le
 * sol « mentirait » a la balistique, qui echantillonne le champ continu.
 * cf. doc/02-terrain-et-bruit.md
 */
const SEGMENTS = 380;

const SCRUB = new Color(0x8a8450);
const SCRUB_DARK = new Color(0x625e33);
const DIRT = new Color(0x9c8a63);
const DIRT_DARK = new Color(0x77674a);
const ROCK = new Color(0x8b8276);
const ROCK_DARK = new Color(0x625b50);
/** Les hauteurs sont de la roche nue et claire, pas de la prairie. */
const HIGHLAND = new Color(0x8d8370);
const DUST = new Color(0xb3a68c);

export function createTerrain(scene: Scene): Mesh {
  const geometry = new PlaneGeometry(WORLD_SIZE, WORLD_SIZE, SEGMENTS, SEGMENTS);
  geometry.rotateX(-Math.PI / 2);

  const pos = geometry.attributes.position as BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    pos.setY(i, terrainHeight(pos.getX(i), pos.getZ(i)));
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();

  const colors = new Float32Array(pos.count * 3);
  const c = new Color();
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const slope = 1 - terrainNormal(x, z).y;
    // Deux echelles de moucheture : sans la seconde, les grandes etendues
    // plates sont d'un beige parfaitement uniforme.
    const mottle = fbm(x * 0.075, z * 0.075, 2, 404) * 0.5 + 0.5;
    const grain = fbm(x * 0.38, z * 0.38, 2, 909) * 0.5 + 0.5;

    // Broussaille clairsemee sur le plat, terre nue la ou elle a ete retournee,
    // roche sur les pentes, neige sur les sommets.
    c.copy(SCRUB).lerp(SCRUB_DARK, mottle * 0.7 + grain * 0.3);
    c.lerp(DIRT.clone().lerp(DIRT_DARK, grain), smoothstep(0.34, 0.72, mottle));
    c.lerp(ROCK.clone().lerp(ROCK_DARK, mottle), smoothstep(0.26, 0.58, slope));
    // Altitude : la broussaille s'arrete et la roche prend le dessus. Pas de
    // neige -- un champ de tir desertique coiffe de blanc, l'oeil n'y croit
    // pas, et les sommets clairs avalaient les cibles lointaines qui s'y
    // detachaient.
    c.lerp(HIGHLAND, smoothstep(20, 76, y + mottle * 12));

    // Lit asseche : poussiere claire, sur le fond plat du bassin seulement.
    // Sans la borne sur `basin`, la trainee remonte le flanc des collines --
    // le champ de hauteur, lui, borne deja son creusement de la meme facon.
    const basin = 1 - smoothstep(190, 250, basinRadius(x, z));
    const wash = Math.exp(-(((x - washCenterX(z)) / 17) ** 2));
    c.lerp(DUST, wash * basin * (1 - smoothstep(0.2, 0.5, slope)) * 0.75);

    const shade = 0.84 + 0.12 * mottle + 0.1 * grain;
    colors[i * 3] = clamp(c.r * shade, 0, 1);
    colors[i * 3 + 1] = clamp(c.g * shade, 0, 1);
    colors[i * 3 + 2] = clamp(c.b * shade, 0, 1);
  }
  geometry.setAttribute('color', new BufferAttribute(colors, 3));

  const material = new MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.95,
    metalness: 0,
  });

  const mesh = new Mesh(geometry, material);
  mesh.receiveShadow = true;
  // castShadow reste a false : le frustum d'ombre suit le char sur 46 metres,
  // les collines n'y projettent rien et on economise 290k triangles par frame.
  mesh.name = 'terrain';
  scene.add(mesh);
  return mesh;
}
